import {
    resolveWebTemplateBlueprint,
    type WebTemplateBlueprint,
} from "./webTemplateBlueprint";

type WebTemplateBlueprintStylesProps = {
    editorMode?: boolean;
    readOnly?: boolean;
    templateCode: string;
};

function cssAttributeValue(value: string): string {
    return value
        .replaceAll("\\", "\\\\")
        .replaceAll('"', '\\"')
        .replace(/[\n\r\f]/gu, " ");
}

function blueprintMotionCss(
    root: string,
    blueprint: WebTemplateBlueprint,
): string {
    const reveal = `${root}:not([data-web-template-editor="true"]) [data-web-builder-region="page"] > .site-builder-section:not([data-web-region="mobileAction"]):not([data-web-region$="Cta"]) > .site-builder-grid`;

    if (blueprint === "torque") {
        return `
            @keyframes web-template-blueprint-torque-enter {
                from {
                    opacity: .18;
                    transform: translate3d(0, 26px, 0);
                }
                to {
                    opacity: 1;
                    transform: translate3d(0, 0, 0);
                }
            }

            @supports (animation-timeline: view()) {
                ${reveal} {
                    animation-duration: 1ms;
                    animation-fill-mode: both;
                    animation-name: web-template-blueprint-torque-enter;
                    animation-range: entry 0% cover 28%;
                    animation-timeline: view(block);
                    animation-timing-function: var(--web-blueprint-ease);
                }
            }
        `;
    }

    return `
        @keyframes web-template-blueprint-nocturne-enter {
            from {
                filter: blur(5px);
                opacity: 0;
                transform: translate3d(0, 18px, 0) scale(.985);
            }
            to {
                filter: blur(0);
                opacity: 1;
                transform: translate3d(0, 0, 0) scale(1);
            }
        }

        @supports (animation-timeline: view()) {
            ${reveal} {
                animation-duration: 1ms;
                animation-fill-mode: both;
                animation-name: web-template-blueprint-nocturne-enter;
                animation-range: entry 0% cover 26%;
                animation-timeline: view(block);
                animation-timing-function: var(--web-blueprint-ease);
            }
        }
    `;
}

function commonBlueprintCss(root: string, motionEnabled: boolean): string {
    const reveal = `${root}:not([data-web-template-editor="true"]) [data-web-builder-region="page"] > .site-builder-section:not([data-web-region="mobileAction"]):not([data-web-region$="Cta"]) > .site-builder-grid`;

    return `
        ${root} {
            --web-blueprint-ease: cubic-bezier(.2, .75, .25, 1);
            --web-blueprint-ease-sharp: cubic-bezier(.18, .8, .2, 1);
            --web-blueprint-shadow-rest: 0 12px 38px rgb(0 0 0 / .08);
            --web-blueprint-shadow-lift: 0 24px 64px rgb(0 0 0 / .16);
            scroll-behavior: smooth;
            scroll-padding-top: clamp(5rem, 9vw, 8rem);
            text-rendering: optimizeLegibility;
        }

        ${root}[data-web-template-editor="true"] {
            scroll-behavior: auto;
        }

        ${root} :focus-visible {
            outline: 2px solid var(--site-accent, var(--site-primary));
            outline-offset: 4px;
        }

        ${root} :where(
            .site-builder-text-h1,
            .site-builder-text-h2,
            .site-builder-text-h3
        ) {
            text-wrap: balance;
        }

        ${root} :where(
            .site-builder-text-paragraph,
            .site-builder-card-description,
            .site-builder-form-description
        ) {
            text-wrap: pretty;
        }

        ${root} .site-builder-section:not([data-web-region="header"]):not([data-web-region="footer"]) {
            isolation: isolate;
            scroll-margin-top: clamp(5rem, 9vw, 8rem);
        }

        ${root} :where(
            [data-document-role="heroVisual"],
            [data-document-role="editorialVisual"],
            [data-document-role="projectVisual"],
            .site-builder-card-media
        ) {
            overflow: hidden;
        }

        ${root} :where(
            [data-document-role="heroVisual"],
            [data-document-role="editorialVisual"],
            [data-document-role="projectVisual"],
            .site-builder-card-media
        ) .site-builder-image {
            transition:
                filter 700ms var(--web-blueprint-ease),
                transform 900ms var(--web-blueprint-ease);
        }

        ${root} .site-builder-card {
            transition:
                border-color 260ms ease,
                box-shadow 360ms var(--web-blueprint-ease),
                transform 360ms var(--web-blueprint-ease);
        }

        ${root} .site-builder-button {
            transition:
                background-color 180ms ease,
                border-color 180ms ease,
                box-shadow 220ms ease,
                color 180ms ease,
                transform 220ms var(--web-blueprint-ease-sharp);
        }

        ${motionEnabled ? "" : `
            ${reveal} {
                animation: none !important;
                filter: none !important;
                opacity: 1 !important;
                transform: none !important;
            }
        `}

        @media (hover: hover) and (pointer: fine) {
            ${root} .site-builder-card:hover,
            ${root} .site-builder-card:focus-within {
                box-shadow: var(--web-blueprint-shadow-lift);
                transform: translate3d(0, -4px, 0);
            }
        }

        @media (max-width: 767px) {
            ${root} {
                background-attachment: scroll;
                scroll-padding-top: 5rem;
            }

            ${root} :where(
                .site-builder-text-h1,
                .site-builder-text-h2,
                .site-builder-text-h3
            ) {
                overflow-wrap: anywhere;
            }

            ${root} .site-builder-button {
                min-height: 48px;
            }

            ${reveal} {
                animation-range: entry 0% cover 18%;
            }
        }

        @media (pointer: coarse) {
            ${root} :where(
                .site-builder-button,
                .site-builder-navigation-link,
                button,
                input,
                select,
                textarea
            ) {
                min-height: 44px;
            }
        }

        @media (prefers-reduced-motion: reduce) {
            ${root} {
                background-attachment: scroll;
                scroll-behavior: auto !important;
            }

            ${reveal} {
                animation: none !important;
                filter: none !important;
                opacity: 1 !important;
                transform: none !important;
            }

            ${root} :where(
                .site-builder-button,
                .site-builder-card,
                .site-builder-image
            ) {
                transition-duration: .01ms !important;
            }

            ${root} .site-builder-button:is(:hover, :focus-visible),
            ${root} .site-builder-card:is(:hover, :focus-within) {
                filter: none !important;
                transform: none !important;
            }
        }
    `;
}

function torqueBlueprintCss(root: string): string {
    return `
        ${root} {
            --web-blueprint-shadow-rest: 0 10px 0 color-mix(in srgb, var(--site-text) 7%, transparent);
            --web-blueprint-shadow-lift: 8px 10px 0 color-mix(in srgb, var(--site-accent) 28%, transparent);
        }

        ${root} ::selection {
            background: var(--site-accent, var(--site-primary));
            color: var(--site-surface, var(--site-background));
        }

        ${root} :where(
            .site-builder-text-h1,
            .site-builder-text-h2
        ) {
            letter-spacing: -.025em;
        }

        ${root} :where(
            [data-document-role="heroEyebrow"],
            [data-document-role="sectionEyebrow"],
            .site-builder-badge
        ) {
            letter-spacing: .11em;
            text-transform: uppercase;
        }

        ${root} .site-builder-button {
            letter-spacing: .075em;
            text-transform: uppercase;
        }

        ${root} .site-builder-card {
            border-color: color-mix(in srgb, var(--site-border) 76%, var(--site-text));
            box-shadow: var(--web-blueprint-shadow-rest);
        }

        ${root} :where(
            [data-document-role="heroVisual"],
            [data-document-role="editorialVisual"],
            [data-document-role="projectVisual"],
            .site-builder-card-media
        ) .site-builder-image {
            filter: saturate(.84) contrast(1.055);
        }

        @media (hover: hover) and (pointer: fine) {
            ${root} .site-builder-button:hover,
            ${root} .site-builder-button:focus-visible {
                transform: translate3d(4px, 0, 0);
            }

            ${root} :where(
                [data-document-role="heroVisual"],
                [data-document-role="editorialVisual"],
                [data-document-role="projectVisual"],
                .site-builder-card-media
            ):is(:hover, :focus-within) .site-builder-image {
                filter: saturate(1.04) contrast(1.04);
                transform: scale(1.025);
            }
        }
    `;
}

function nocturneBlueprintCss(root: string): string {
    return `
        ${root} {
            --web-blueprint-shadow-rest: 0 16px 48px rgb(0 0 0 / .12);
            --web-blueprint-shadow-lift:
                0 28px 78px rgb(0 0 0 / .2),
                0 0 34px color-mix(in srgb, var(--site-primary) 14%, transparent);
            background-attachment: fixed;
            background-image:
                radial-gradient(circle at 12% 12%, color-mix(in srgb, var(--site-primary) 7%, transparent), transparent 32rem),
                radial-gradient(circle at 88% 30%, color-mix(in srgb, var(--site-accent) 6%, transparent), transparent 34rem);
        }

        ${root} ::selection {
            background: var(--site-primary);
            color: var(--site-background);
        }

        ${root} :where(
            .site-builder-text-h1,
            .site-builder-text-h2
        ) {
            letter-spacing: -.018em;
        }

        ${root} :where(
            [data-document-role="heroEyebrow"],
            [data-document-role="sectionEyebrow"],
            .site-builder-badge
        ) {
            letter-spacing: .13em;
            text-transform: uppercase;
        }

        ${root} .site-builder-card {
            backdrop-filter: blur(10px);
            border-color: color-mix(in srgb, var(--site-border) 76%, var(--site-primary));
            box-shadow: var(--web-blueprint-shadow-rest);
        }

        ${root} :where(
            [data-document-role="heroVisual"],
            [data-document-role="editorialVisual"],
            [data-document-role="projectVisual"],
            .site-builder-card-media
        ) .site-builder-image {
            filter: saturate(1.04) contrast(1.035);
        }

        @media (hover: hover) and (pointer: fine) {
            ${root} .site-builder-button:hover,
            ${root} .site-builder-button:focus-visible {
                box-shadow: 0 12px 34px color-mix(in srgb, var(--site-primary) 24%, transparent);
                transform: translate3d(0, -2px, 0);
            }

            ${root} :where(
                [data-document-role="heroVisual"],
                [data-document-role="editorialVisual"],
                [data-document-role="projectVisual"],
                .site-builder-card-media
            ):is(:hover, :focus-within) .site-builder-image {
                filter: saturate(1.14) contrast(1.04);
                transform: scale(1.045);
            }
        }
    `;
}

export function WebTemplateBlueprintStyles({
    editorMode,
    readOnly,
    templateCode,
}: WebTemplateBlueprintStylesProps) {
    const blueprint = resolveWebTemplateBlueprint(templateCode);
    const root = `:where([data-web-template="${cssAttributeValue(templateCode)}"])`;
    const motionEnabled = !editorMode;
    const blueprintCss = blueprint === "torque"
        ? torqueBlueprintCss(root)
        : nocturneBlueprintCss(root);
    const css = [
        commonBlueprintCss(root, motionEnabled),
        blueprintCss,
        motionEnabled ? blueprintMotionCss(root, blueprint) : "",
    ].join("\n");

    return (
        <style
            data-web-template-blueprint={blueprint}
            data-web-template-blueprint-motion={motionEnabled ? "view" : "disabled"}
            data-web-template-blueprint-read-only={readOnly ? "true" : undefined}
            data-web-template-blueprint-styles="true"
        >
            {css}
        </style>
    );
}
